import { Box, Text } from '@chakra-ui/react';
import { format } from 'd3-format';
import { isNumber } from 'lodash';
import { useEffect, useRef } from 'react';

import { useResizeObserver } from '@revelio/core';
import type { GetSentimentDataQuery } from '@revelio/data-access';
import {
  BarChart,
  FormatType,
  NEGATIVE_COLOR,
  POSITIVE_COLOR,
} from '@revelio/replots';

import { DirectionArrows } from '../../../sentiment/sentimentV2/components/sentiment-effects-charts/direction-arrows';
import { normalizeBarScoresData } from '../../../sentiment/sentimentV2/hooks/use-get-sentiment-reviews-data';
import type { SentimentBarDatum } from '../../../sentiment/sentimentV2/types';
import { transformToSingleEntity } from '../../../sentiment/sentimentV2/utils/transform-to-single-entity';
import type { PrimaryFilterId } from '../../filters/types/filters';
import { SentimentDataProviderRaw } from '../DataProvider/SentimentDataProvider';
import {
  DataProviderQueryStatus,
  DataType,
  type SingleDataSet,
} from '../DataProvider/types';
import { ChartFallback } from './ChartFallback';
import { useChartMenuContext } from './chart-menu/ChartMenuContext';

const formatNumber = format('.0%');

type SentimentEffectBarProps = {
  data: GetSentimentDataQuery | undefined;
  colors?: string[];
  leftLabel?: string;
  rightLabel?: string;
  fetching?: boolean;
};

const SentimentEffectBar = ({
  data,
  leftLabel = 'Factors of Attrition',
  rightLabel = 'Factors of Hiring',
  fetching = false,
}: SentimentEffectBarProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver(containerRef);
  const { setDataProviderSet } = useChartMenuContext();

  const barData: SentimentBarDatum[] = (() => {
    if (!data) return [];
    const normalizedData = normalizeBarScoresData(data);
    return transformToSingleEntity(normalizedData).data;
  })();

  useEffect(() => {
    if (!fetching && barData) {
      const singleDataSet: SingleDataSet = {
        type: DataType.SINGLE,
        data: barData.map((item) => ({
          dim: item.label,
          value: item.count ?? 0,
          share: (item.value ?? 0) * 100,
        })),
      };

      setDataProviderSet(singleDataSet);
    }
  }, [fetching, barData, setDataProviderSet]);

  // Color logic: positive = POSITIVE_COLOR, negative = NEGATIVE_COLOR
  const barDataWithColors = barData.map((d) => {
    const color = d.value && d.value >= 0 ? POSITIVE_COLOR : NEGATIVE_COLOR;

    return {
      ...d,
      color,
    };
  });

  const [min, max] = [Math.min, Math.max].map((fn) =>
    fn(...barData.map((d) => ('value' in d ? (d.value ?? 0) : 0)))
  );

  return (
    <Box
      ref={containerRef}
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <Box flex="1 1 auto" minHeight={0}>
        <BarChart
          data={barDataWithColors}
          format={FormatType.PERCENTAGE}
          // @ts-expect-error noUncheckedIndexedAccess
          xAxis={{ min, max }}
          showBarLabels={false}
          tooltipContent={(data) => {
            const ttValue = data.rawValue ?? data.value;
            return (
              <div>
                <Text fontWeight="semibold" color="#fff" fontSize="xs">
                  {`${data.label}: ${isNumber(ttValue) ? formatNumber(ttValue) : '-'}`}
                </Text>
                <Text color="#fff" fontSize="xs">
                  {`(n: ${data.count})`}
                </Text>
              </div>
            );
          }}
        />
      </Box>
      <Box width="100%" height="25px">
        <DirectionArrows
          width={width}
          leftLabel={leftLabel}
          rightLabel={rightLabel}
        />
      </Box>
    </Box>
  );
};

export const SentimentEffectBarChart = ({
  primaryDimension,
}: {
  primaryDimension: PrimaryFilterId;
}) => {
  return (
    <SentimentDataProviderRaw primaryDimension={primaryDimension} isSnapshot>
      {({ data, status }) => {
        return (
          <ChartFallback status={status}>
            <SentimentEffectBar
              data={data}
              fetching={status === DataProviderQueryStatus.Fetching}
            />
          </ChartFallback>
        );
      }}
    </SentimentDataProviderRaw>
  );
};
